import { useEffect } from 'react'
import '../DocumentPage.css'

const CsvGeneration = () => {
  useEffect(() => {
    const headings = document.querySelectorAll('.doc-content h2, .doc-content h3')
    headings.forEach((heading) => {
      if (!heading.id) {
        heading.id = heading.textContent.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')
      }
    })
  }, [])

  return (
    <div className="doc-page">
      <div className="doc-content">
        <h1>CSV Generation</h1>
        <p className="lead">
          Learn how to generate, import, and manage CSV files for your game variables.
        </p>

        <h2>What is CSV Generation?</h2>
        <p>
          CSV (Comma-Separated Values) generation is the process of creating structured data files that
          can be easily edited in spreadsheet applications and imported into Unreal Engine.
        </p>

        <h2>Generating CSV Files</h2>
        <h3>Automatic Generation</h3>
        <p>The Variable Modding System can automatically generate CSV templates:</p>
        <ol>
          <li>Open the Variable Modding System tools</li>
          <li>Select <strong>Generate CSV Template</strong></li>
          <li>Choose your data structure</li>
          <li>Specify the output location</li>
          <li>Click Generate</li>
        </ol>

        <h3>Manual Creation</h3>
        <p>You can also create CSV files manually:</p>
        <pre><code>{`VariableName,Type,DefaultValue,Description
PlayerHealth,Float,100.0,Maximum player health
PlayerSpeed,Float,600.0,Player movement speed
MaxJumpHeight,Float,500.0,Maximum jump height`}</code></pre>

        <h2>Importing CSV Files</h2>
        <p>To import a CSV file into Unreal Engine:</p>
        <ol>
          <li>Right-click in the Content Browser</li>
          <li>Select <strong>Import to /Content/</strong></li>
          <li>Choose your CSV file</li>
          <li>Select the appropriate Data Table structure</li>
          <li>Confirm the import</li>
        </ol>

        <h2>Exporting Data Tables to CSV</h2>
        <p>You can export existing Data Tables back to CSV:</p>
        <ol>
          <li>Right-click on a Data Table asset</li>
          <li>Select <strong>Export as CSV</strong></li>
          <li>Choose the export location</li>
          <li>Save the file</li>
        </ol>

        <h2>CSV Format Guidelines</h2>
        <h3>Column Headers</h3>
        <p>The first row must contain column headers matching your data structure.</p>

        <h3>Data Types</h3>
        <p>Supported data types include:</p>
        <ul>
          <li><code>String</code> - Text values</li>
          <li><code>Float</code> - Decimal numbers</li>
          <li><code>Integer</code> - Whole numbers</li>
          <li><code>Boolean</code> - True/False values</li>
          <li><code>Vector</code> - 3D coordinates</li>
        </ul>

        <h3>Special Characters</h3>
        <p>Handle special characters properly:</p>
        <ul>
          <li>Use quotes for text containing commas</li>
          <li>Escape quotes with double quotes</li>
          <li>Use UTF-8 encoding for international characters</li>
        </ul>

        <h2>Best Practices</h2>
        <ul>
          <li>Always validate CSV files before importing</li>
          <li>Keep backups of your CSV data</li>
          <li>Use version control for CSV files</li>
          <li>Document column meanings in a separate file</li>
          <li>Test imports with small datasets first</li>
        </ul>

        <h2>Troubleshooting</h2>
        <h3>Import Errors</h3>
        <p>Common issues and solutions:</p>
        <ul>
          <li><strong>Mismatched columns</strong>: Ensure headers match your struct definition</li>
          <li><strong>Invalid data types</strong>: Check that values match expected types</li>
          <li><strong>Encoding issues</strong>: Save CSV as UTF-8</li>
          <li><strong>Extra commas</strong>: Quote text fields containing commas</li>
        </ul>
      </div>
    </div>
  )
}

export default CsvGeneration
